/**
 * Spec 028: a trip's distance allowance in words, in the trip's unit
 * (`receipt.distanceUnit`, from `useBookingLocale`). Mile text is today's;
 * an unlimited allowance reads "Unlimited distance" on every brand (spec Q5).
 * Shared by the receipt modal and the trip page.
 */
import { asDistanceUnit, convertLimit, DistanceUnit, formatDistance } from './units';

interface TripLike {
  receipt?: {
    allowedMileage?: number | null;
    rentalType?: string | null;
    totalDays?: number | null;
  } | null;
  car?: {
    distanceLimit?: {
      dailyDistanceLimit?: number | null;
      unit?: string | null;
    } | null;
  } | null;
}

export interface AllowanceDisplay {
  value: string;
  note: string;
}

export function tripAllowanceDisplay(
  booking: TripLike | null | undefined,
  unit: DistanceUnit,
  options: { skipEmptyFallback?: boolean } = {},
): AllowanceDisplay | null {
  const receipt = booking?.receipt;
  const allowedMileage = receipt?.allowedMileage;
  const km = unit === 'km';

  if (allowedMileage === -1) {
    return { value: 'Unlimited distance', note: 'Unlimited distance for this trip' };
  }

  if (allowedMileage != null && !Number.isNaN(Number(allowedMileage))) {
    const allowed = Number(allowedMileage);
    if (allowed > 0) {
      const monthly = receipt?.rentalType === 'MONTHLY';
      return {
        value: formatDistance(allowed, unit, 'allowance'),
        note: km
          ? monthly
            ? 'Monthly distance allowance for this trip'
            : 'Daily distance allowance for this trip'
          : monthly
            ? 'Monthly mileage allowance for this trip'
            : 'Daily mileage allowance for this trip',
      };
    }
  }

  // No snapshot: the car's own daily limit, in the trip's unit.
  const limit = booking?.car?.distanceLimit;
  const daily = convertLimit(
    Number(limit?.dailyDistanceLimit ?? 0),
    asDistanceUnit(limit?.unit),
    unit,
  );
  const total = daily * Number(receipt?.totalDays ?? 0);
  if (options.skipEmptyFallback && total <= 0) return null;
  return {
    value: formatDistance(total, unit, 'plain').replace(/ mile$/, ' miles'),
    note: `Distance limited to ${formatDistance(daily, unit, 'perDay')}`,
  };
}

/** Spec 028: the checkout line about the allowance, in the trip's unit. */
export function checkoutAllowanceText(allowedMileage: number, unit: DistanceUnit): string {
  if (allowedMileage === -1) return 'Unlimited distance for this trip';
  return unit === 'km'
    ? `Allowed distance for this trip: ${formatDistance(allowedMileage, 'km', 'allowance')}`
    : `Allowed mileage for this trip: ${allowedMileage.toLocaleString()} miles`;
}
